import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Autocomplete, Box, Button, TextField, Typography } from "@mui/material";
import { Input } from "../Input";
import { ManualRegionDialog } from "../ManualRegionDialog";
import { COUNTRIES, statesForCountry } from "../../../constants/geoLists";
import "./styles.scss";

/**
 * Shared manual-address controls used by every address editor (home address,
 * contact address, visit-report address). A "manual" address is one a user
 * hand-enters for a place no geocoder can resolve.
 *
 * When manual mode is ON the user must notice it (visible badge + warning) and
 * the geocoder is bypassed: country / state / city and the coordinates become
 * editable here, plus an optional "nearby road" routing point and a per-address
 * optimization include/exclude override.
 *
 * Country and State are STRICT closed-list autocompletes (no free text): a typo
 * there would silently break route optimization, which matches a manual address to
 * its per-region routing graph by country + state. State only appears for countries
 * that route per-state (US / UK / CA / AU); elsewhere it is hidden.
 *
 * The component is path-agnostic: the parent supplies the address `value` and a
 * `setField(leaf, value)` callback already bound to the address's formik path,
 * so the same component works for `homeAddress.*` and `persons[i].addresses[j].*`.
 */
export interface ManualAddressValue {
  is_manual?: boolean;
  country?: string;
  state?: string;
  city?: string;
  street_name?: string;
  /** The hand-typed address text — persisted here (the single text column) and NOT scattered
   *  into street_name/city/etc. `fullAddress` is the form-only display mirror. */
  formatted_address?: string;
  fullAddress?: string;
  full_address?: string;
  place_id?: string;
  latitude?: string | number;
  longitude?: string | number;
  routing_latitude?: string | number;
  routing_longitude?: string | number;
  include_in_optimize?: boolean | null;
  /** Per-address "Don't visit" flag — excludes this address's pin from the map and from
   *  newly built routes. Lives on the address (not the household) since a household can
   *  have multiple addresses (home + each contact's work address) that must be excluded
   *  independently. */
  is_do_not_visit?: boolean;
}

interface IProps {
  value: ManualAddressValue;
  setField: (leaf: string, value: unknown) => void;
  /** Translation getter scoped to the `contact_info` namespace. The optional second
   *  arg is forwarded to i18next `t` for interpolation (e.g. `{ current: "included" }`). */
  p: (key: string, options?: Record<string, unknown>) => string;
  /**
   * Country / state to pre-fill when manual mode is switched on and they are empty —
   * sourced by the host from the workspace's "manual default region" setting (a
   * region-level Google pick that carries only a country + state), so the user
   * rarely has to type them. City is not pre-filled (the region setting has none).
   */
  defaultCountry?: string;
  defaultState?: string;
  /** Called after the manual flag is toggled, so a host can set type/primary, etc. */
  onToggle?: (checked: boolean) => void;
  /** Validation messages to show under the coordinate fields (e.g. "required" when a manual
   *  address is missing them). Supplied by hosts whose form validates these via formik. */
  latitudeError?: string;
  longitudeError?: string;
}

const toStr = (v: unknown) => (v === null || v === undefined ? "" : String(v));

export const ManualAddressSection: FC<IProps> = ({ value, setField, p, defaultCountry, defaultState, onToggle, latitudeError, longitudeError }) => {
  // Admin master switch: when manual addresses are disabled, hide the control entirely —
  // unless this address is already manual, so existing manual data stays visible/editable.
  const manualEnabled = useSelector((s: any) => s.authStore?.workspace?.manual_addresses_enabled ?? true);
  // The workspace default the per-address "inherit" option falls back to — shown
  // live in that option's label so the user knows what choosing it actually does.
  const optimizeDefaultOn = useSelector((s: any) => s.authStore?.workspace?.include_manual_in_optimize ?? true);

  // "Configure now" popup for setting the workspace default region.
  const [regionDialogOpen, setRegionDialogOpen] = useState(false);

  const isManual = !!value.is_manual;
  const country = toStr(value.country);
  const stateOptions = statesForCountry(country);
  const showState = stateOptions.length > 0;

  // A manual address can only be entered once the workspace has a default region — it supplies
  // the (now hidden) Country/State that key the routing graph. The gate is simply "a region was
  // set in Settings": ANY country is valid (ConnectYid supports the whole world via Google), and
  // the Settings region picker already guarantees a real, list-picked country, so we trust it
  // here rather than re-filtering against the bundled COUNTRIES list (which would hide the
  // coordinate inputs for a legitimately-saved country the list doesn't enumerate).
  const hasRegion = !!toStr(defaultCountry).trim();

  // The hand-typed address text, kept visible and saved as plain text in a SINGLE column
  // (formatted_address). fullAddress is the form-only display mirror.
  const addressText = toStr(value.fullAddress) || toStr(value.formatted_address) || toStr(value.street_name);
  const onAddressTextChange = (text: string) => {
    // The manual address text is OPTIONAL: it may be left blank while staying in manual mode.
    // Emptying it only clears the text — it must NEVER exit manual mode or touch the hand-entered
    // coordinates. (Deleting the whole address has its own explicit controls: the Delete-address
    // button / trash icon.) The text persists as opaque text in ONE column (formatted_address);
    // fullAddress is the display mirror. It is deliberately NOT written into street_name/
    // full_address (or any other structured column), and any leftover street_name from a prior
    // Google pick / legacy manual row is cleared so the text never lives in two columns at once.
    setField("fullAddress", text);
    setField("formatted_address", text);
    if (value.street_name) setField("street_name", "");
  };

  // Country/State for a manual address come ONLY from the workspace Default Region — never from
  // whatever a prior Google pick left behind. Whenever manual mode is on, OVERWRITE them with the
  // region's values (only a RECOGNIZED country is used, never a state like "Texas"; state only when
  // it is a valid option for that country). These fields are hidden from the user but must stay
  // populated as the routing keys route division selects households by.
  // Depend on defaultCountry/defaultState too — NOT just isManual. Otherwise, if the user toggles
  // manual ON before a Default Region exists (the "Configure now" flow) and sets the region
  // afterwards, this effect would never re-run and the address would be saved with no country/state.
  useEffect(() => {
    if (!isManual) return;
    if (defaultCountry) {
      if (country !== defaultCountry) setField("country", defaultCountry);
      const seededState = defaultState && statesForCountry(defaultCountry).includes(defaultState) ? defaultState : "";
      if (toStr(value.state) !== seededState) setField("state", seededState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManual, defaultCountry, defaultState]);

  if (!manualEnabled && !isManual) {
    return null;
  }

  // Switching INTO manual mode collapses the chosen address into the single free-text box and
  // wipes everything else that must not persist scattered: the geocoded pin identity (coordinates
  // + place_id) AND the structured columns a prior Google pick left behind (street/city/zip/apt/
  // unit). Only the collapsed text (formatted_address/fullAddress) survives; Country/State are then
  // overwritten from the workspace default region as the hidden routing keys.
  const MANUAL_CLEARED_FIELDS = [
    "latitude", "longitude", "place_id",
    "street_name", "city", "zip_code", "apartment_number", "unit_number",
  ];
  // Leaving manual mode resets the hand-entered coordinates so the user must re-pick from Google;
  // the text stays so it repopulates the host's autocomplete for re-selection.
  const MANUAL_EXIT_CLEARED_FIELDS = ["latitude", "longitude"];

  const onToggleChange = (checked: boolean) => {
    setField("is_manual", checked);
    if (checked) {
      // Manual and text-only are mutually exclusive — entering manual mode must always
      // leave text-only mode too, or unchecking Manual later would silently fall back to
      // a stale text-only summary the user never re-confirmed.
      setField("is_text_only", false);
      MANUAL_CLEARED_FIELDS.forEach((leaf) => setField(leaf, ""));
      // Country/State come ONLY from the default region — overwrite any Google-derived values.
      if (defaultCountry) {
        setField("country", defaultCountry);
        setField("state", defaultState && statesForCountry(defaultCountry).includes(defaultState) ? defaultState : "");
      }
    } else {
      MANUAL_EXIT_CLEARED_FIELDS.forEach((leaf) => setField(leaf, ""));
    }
    onToggle?.(checked);
  };

  const onCountryChange = (next: string | null) => {
    const nextCountry = next || "";
    setField("country", nextCountry);
    // Dropping to a country whose state list no longer contains the current state
    // would leave a stale value that can mis-key the routing graph — clear it.
    const nextStates = statesForCountry(nextCountry);
    if (value.state && !nextStates.includes(value.state)) {
      setField("state", "");
    }
  };

  // Pasting "lat, lng" into the latitude field splits it across both coordinates —
  // a common copy from Google Maps. A single value is stored as-is.
  const onLatitudeChange = (raw: string) => {
    const parts = raw.split(",");
    if (parts.length === 2 && parts[0].trim() !== "" && parts[1].trim() !== "") {
      setField("latitude", parts[0].trim());
      setField("longitude", parts[1].trim());
      return;
    }
    setField("latitude", raw);
  };

  // include_in_optimize: null/undefined = inherit workspace default.
  const includeValue =
    value.include_in_optimize === true
      ? "include"
      : value.include_in_optimize === false
      ? "exclude"
      : "inherit";
  const onIncludeChange = (v: string) => {
    setField("include_in_optimize", v === "include" ? true : v === "exclude" ? false : null);
  };

  // Ensure the current saved value is always selectable even if it isn't in the
  // bundled list (e.g. a country the list doesn't enumerate), so MUI doesn't warn.
  const countryOptions = country && !COUNTRIES.includes(country) ? [country, ...COUNTRIES] : COUNTRIES;
  const stateValue = toStr(value.state);
  const stateSelectable =
    stateValue && !stateOptions.includes(stateValue) ? [stateValue, ...stateOptions] : stateOptions;

  return (
    <Box sx={{ mt: 3, pt: 2, pb: 2, mb: 1, borderTop: "1px solid #E6E8F0" }}>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
          fontSize: "13px",
          lineHeight: 1.6,
          color: "#0E0D5B",
          cursor: "pointer",
          padding: "4px 0",
        }}
      >
        <input type="checkbox" checked={isManual} onChange={(e) => onToggleChange(e.target.checked)} />
        {p("manual_address")}
        {isManual && (
          <Box
            component="span"
            sx={{
              ml: 0.5,
              px: 1,
              py: "1px",
              borderRadius: "8px",
              backgroundColor: "#FDECEA",
              color: "#B71C1C",
              fontSize: "11px",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {p("manual_badge")}
          </Box>
        )}
      </label>

      {/* Bare checkbox, no helper text — excludes THIS address's pin from the map and from
          newly built routes. Independent per address (home vs. each contact's work address). */}
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: "13px",
          lineHeight: 1.6,
          color: "#0E0D5B",
          cursor: "pointer",
          padding: "4px 0",
        }}
      >
        <input
          type="checkbox"
          checked={!!value.is_do_not_visit}
          onChange={(e) => setField("is_do_not_visit", e.target.checked)}
        />
        {p("dont_visit_address")}
      </label>

      {isManual && (
        <Box sx={{ mt: 1.5, p: 2, border: "1px solid #E6E8F0", borderRadius: "10px", backgroundColor: "#FCFCFE" }}>
          {/* The hand-typed address text stays visible and is saved as plain text. */}
          <Box sx={{ mb: 2 }}>
            <Input
              label={p("address")}
              placeholder={p("address")}
              value={addressText}
              onChange={(e) => onAddressTextChange(e.target.value)}
            />
          </Box>

          {!hasRegion ? (
            // No workspace default region yet — a manual address can't be keyed to a
            // routing graph without it, so prompt the admin to set one first.
            <Box>
              <Typography sx={{ fontSize: "13px", color: "#6B6F94", mb: 1.5 }}>
                {p("manual_region_required")}
              </Typography>
              <Button variant="contained" size="small" onClick={() => setRegionDialogOpen(true)}>
                {p("configure_now")}
              </Button>
            </Box>
          ) : (
            // Region is set → only the coordinates need entering by hand.
            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Input
                  label={p("latitude")}
                  placeholder={p("latitude_placeholder")}
                  value={toStr(value.latitude)}
                  onChange={(e) => onLatitudeChange(e.target.value)}
                  isRequired
                  error={latitudeError}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Input
                  label={p("longitude")}
                  placeholder={p("longitude_placeholder")}
                  value={toStr(value.longitude)}
                  onChange={(e) => setField("longitude", e.target.value)}
                  isRequired
                  error={longitudeError}
                />
              </Box>
            </Box>
          )}

          {/* ── Hidden from end users (kept for restore) ──────────────────────────
              City, Country, State, routing-point coordinates, the include-in-optimize
              override and the explanatory hints used to render here. Country/State are
              still populated behind the scenes from the workspace default region (see the
              seeding effect above) so route optimization keeps working — they are simply
              not shown. Re-enable by un-commenting this block.

          <Typography sx={{ fontSize: "12px", color: "#B71C1C", mb: 2 }}>{p("manual_address_hint")}</Typography>

          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Input
                label={p("city")}
                placeholder={p("city")}
                value={toStr(value.city)}
                onChange={(e) => setField("city", e.target.value)}
              />
            </Box>
            <Box sx={{ flex: 1 }} className="home-page-input">
              <label className="home-page-input__label">
                {p("country")}
                <span style={{ color: "red" }}>*</span>
              </label>
              <Autocomplete
                className="manual-address__autocomplete"
                options={countryOptions}
                value={country || null}
                onChange={(_, next) => onCountryChange(next)}
                renderInput={(params) => <TextField {...params} placeholder={p("country")} />}
              />
            </Box>
          </Box>

          {showState && (
            <Box sx={{ width: "100%", mb: 2 }} className="home-page-input">
              <label className="home-page-input__label">
                {p("state")}
                <span style={{ color: "red" }}>*</span>
              </label>
              <Autocomplete
                className="manual-address__autocomplete"
                options={stateSelectable}
                value={stateValue || null}
                onChange={(_, next) => setField("state", next || "")}
                renderInput={(params) => <TextField {...params} placeholder={p("state")} />}
              />
            </Box>
          )}

          <Typography sx={{ fontSize: "11px", color: "#6B6F94", mb: 2 }}>{p("coordinates_range_hint")}</Typography>

          <Typography sx={{ fontSize: "12px", color: "#6B6F94", mb: 1 }}>{p("routing_coordinates_hint")}</Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Input
                label={p("routing_latitude")}
                placeholder="e.g. 31.7700"
                value={toStr(value.routing_latitude)}
                onChange={(e) => setField("routing_latitude", e.target.value)}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Input
                label={p("routing_longitude")}
                placeholder="e.g. -99.4200"
                value={toStr(value.routing_longitude)}
                onChange={(e) => setField("routing_longitude", e.target.value)}
              />
            </Box>
          </Box>

          <Box className="home-page-input">
            <label className="home-page-input__label">{p("include_in_optimization")}</label>
            <select
              className="manual-address__select"
              value={includeValue}
              onChange={(e) => onIncludeChange(e.target.value)}
            >
              <option value="inherit">
                {p("inherit", {
                  current: optimizeDefaultOn ? p("optimization_included") : p("optimization_excluded"),
                })}
              </option>
              <option value="include">{p("include")}</option>
              <option value="exclude">{p("exclude")}</option>
            </select>
          </Box>
          ────────────────────────────────────────────────────────────────────── */}
        </Box>
      )}

      <ManualRegionDialog open={regionDialogOpen} onClose={() => setRegionDialogOpen(false)} />
    </Box>
  );
};
